<template>
  <div
    v-scroll="{scrollOn: scroll.scrollOn, speed: scroll.speed}"
    class="section section-secondary section--border section--padding"
  >
    <div class="container">
      <div class="row">
        <div class="col-lg-6 offset-lg-3">
          <div
            v-show="loading.active && loading.type === 'throbber'"
            class="throbber mt-3"
          >
            <lottie-animation
              id="spinner"
              path="/lottie/spinner.json"
              :autoplay="true"
              :loop="true"
            />
          </div>
          <div v-show="!submitted && !loading.active">
            <form
              novalidate
              autocomplete="off"
              @submit.prevent="onSubmit"
            >
              <fieldset>
                <legend class="legend">
                  {{ get('fieldset.legend') }}
                </legend>
                <div
                  class="form-group"
                  :class="toggleClass('name')"
                >
                  <label for="name">
                    {{ get('fieldset.name.label') }}
                  </label>
                  <div class="field-input">
                    <input
                      id="name"
                      v-model.trim="v$.form.name.$model"
                      type="text"
                      :placeholder="get('fieldset.name.placeholder')"
                      @keydown.enter="onKeydownEnter"
                      @focus="toggleFocus('focus', 'name')"
                      @blur="toggleFocus('blur', 'name')"
                    >
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-6">
                    <div
                      class="form-group"
                      :class="toggleClass('street')"
                    >
                      <label for="name">
                        {{ get('fieldset.street.label') }}
                      </label>
                      <div class="field-input">
                        <input
                          id="street"
                          v-model.trim="v$.form.street.$model"
                          type="text"
                          :placeholder="get('fieldset.street.placeholder')"
                          @keydown.enter="onKeydownEnter"
                          @focus="toggleFocus('focus', 'street')"
                          @blur="toggleFocus('blur', 'street')"
                        >
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-3">
                    <div
                      class="form-group"
                      :class="toggleClass('houseNr')"
                    >
                      <label for="houseNr">
                        {{ get('fieldset.house-number.label') }}
                      </label>
                      <div class="field-input">
                        <input
                          id="houseNr"
                          v-model.trim="v$.form.houseNr.$model"
                          type="text"
                          :placeholder="get('fieldset.house-number.placeholder')"
                          @keydown.enter="onKeydownEnter"
                          @focus="toggleFocus('focus', 'houseNr')"
                          @blur="toggleFocus('blur', 'houseNr')"
                        >
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-3">
                    <div
                      class="form-group"
                      :class="toggleClass('addition')"
                    >
                      <label for="addition">
                        {{ get('fieldset.addition.label') }}
                      </label>
                      <div class="field-input">
                        <input
                          id="addition"
                          v-model.trim="v$.form.addition.$model"
                          type="text"
                          :placeholder="get('fieldset.addition.placeholder')"
                          @keydown.enter="onKeydownEnter"
                          @focus="toggleFocus('focus', 'addition')"
                          @blur="toggleFocus('blur', 'addition')"
                        >
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-6">
                    <div
                      class="form-group"
                      :class="toggleClass('postalCode')"
                    >
                      <label for="postalCode">
                        {{ get('fieldset.postal-code.label') }}
                      </label>
                      <div class="field-input">
                        <input
                          id="postalCode"
                          v-model.trim="v$.form.postalCode.$model"
                          type="text"
                          :placeholder="get('fieldset.postal-code.placeholder')"
                          @keydown.enter="onKeydownEnter"
                          @focus="toggleFocus('focus', 'postalCode')"
                          @blur="toggleFocus('blur', 'postalCode')"
                        >
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-6">
                    <div
                      class="form-group"
                      :class="toggleClass('city')"
                    >
                      <label for="name">
                        {{ get('fieldset.city.label') }}
                      </label>
                      <div class="field-input">
                        <input
                          id="city"
                          v-model.trim="v$.form.city.$model"
                          type="text"
                          :placeholder="get('fieldset.city.placeholder')"
                          @keydown.enter="onKeydownEnter"
                          @focus="toggleFocus('focus', 'city')"
                          @blur="toggleFocus('blur', 'city')"
                        >
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="toggleClass('phone')"
                >
                  <label for="phone">
                    {{ get('fieldset.phone.label') }}
                  </label>
                  <div class="field-input">
                    <input
                      id="phone"
                      v-model.trim="v$.form.phone.$model"
                      type="text"
                      :placeholder="get('fieldset.phone.placeholder')"
                      @keydown.enter="onKeydownEnter"
                      @focus="toggleFocus('focus', 'phone')"
                      @blur="toggleFocus('blur', 'phone')"
                    >
                  </div>
                </div>
                <div
                  class="form-group"
                  :class="toggleClass('email')"
                >
                  <label for="email">
                    {{ get('fieldset.email.label') }}
                  </label>
                  <div class="field-input">
                    <input
                      id="email"
                      v-model.trim="v$.form.email.$model"
                      type="email"
                      :placeholder="get('fieldset.email.placeholder')"
                      @keydown.enter="onKeydownEnter"
                      @focus="toggleFocus('focus', 'email')"
                      @blur="toggleFocus('blur', 'email')"
                    >
                  </div>
                </div>
                <div
                  v-show="hasAnswers"
                  class="form-group"
                >
                  <div class="field-option">
                    <input
                      id="answers"
                      v-model.trim="v$.form.answers.$model"
                      type="checkbox"
                      @change="onAnswersChange()"
                    >
                    <label for="answers">
                      {{ get('fieldset.answers.label') }}
                    </label>
                  </div>
                </div>
                <div class="form-group mb-3">
                  <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="v$.form.$invalid"
                  >
                    {{ get('submit') }}
                  </button>
                </div>
                <div class="form-group mb-5 ts-text--xs">
                  {{ get('required') }}
                </div>
              </fieldset>
            </form>
            <contact-options
              :text="$t('contact-options')"
              :options="contact"
              :calculate-width="calculateWidth"
            />
          </div>
          <div v-show="submitted && !loading.active">
            <p>{{ response }}</p>
            <a
              href=""
              @click.prevent="onBackClick"
            >{{ link }}</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, numeric, email } from '@vuelidate/validators'

import store from 'domain/store/index'
import { mapState } from 'vuex'

import ContactOptions from 'app/components/shared/contact-options'
import LottieAnimation from 'app/components/shared/lottie-animation'

import questionHelper from 'app/helpers/question'
import tenantHelper from 'app/helpers/tenant'
import routeHelper from 'app/helpers/route'

import form from 'app/mixin/form'
import datalayer from 'app/mixin/datalayer'

export default {
  name: 'ContactForm',
  components: {
    ContactOptions,
    LottieAnimation
  },
  mixins: [form, datalayer],
  beforeRouteEnter (to, from, next) {
    const formId = to.params.formId || null

    store.dispatch('getFormInfo', formId).then(function () {
      next()
    })
  },
  setup () {
    return { v$: useVuelidate() }
  },
  data () {
    return {
      data: this.$tm('contact'),
      headerText: tenantHelper.get('payoff'),
      formId: null,
      form: {
        name: '',
        street: '',
        houseNr: '',
        city: '',
        postalCode: '',
        addition: '',
        phone: '',
        email: '',
        answers: false
      },
      submitted: false,
      response: this.$t('contact.form.response.success'),
      link: this.$t('contact.form.link'),
      scroll: {
        scrollOn: 'init',
        speed: 600
      },
      contactOptions: [],
      calculateWidth: true
    }
  },
  computed: {
    ...mapState([
      'loading',
      'answers',
      'contact'
    ]),
    hasAnswers () {
      return this.answers.length
    }
  },
  validations () {
    return {
      form: {
        name: {
          required
        },
        street: {
          required
        },
        houseNr: {
          required,
          numeric
        },
        addition: {},
        postalCode: {
          required
        },
        city: {
          required
        },
        phone: {
          required,
          numeric
        },
        email: {
          required,
          email
        },
        answers: {}
      }
    }
  },
  methods: {
    processData () {
      const data = Object.assign({}, this.form)

      data.regulationId = (this.$route.params.id || null)

      if (this.form.answers) {
        data.answers = questionHelper.getUserAnswers(this.answers, ['first', 'last'])
      } else {
        data.answers = []
      }

      this.$store.dispatch('submitForm', {
        name: 'contact',
        data
      }).then(() => {
        const title = routeHelper.getPageTitle('contact-form-submitted')
        const path = this.$route.path + '/submitted'

        this.submitted = true
        this.$gtm.trackView(title, path)
      })
    },
    onAnswersChange () {
      if (!this.form.answers) return

      this.gtmTrackEvent({
        category: 'Contact',
        action: 'Include my answers',
        label: `Form (id: ${this.formId})`
      })
    },
    onSubmit () {
      const formId = this.$route.params.formId

      this.gtmTrackEvent({
        category: 'Contact',
        action: 'Submit form',
        label: `Form (id: ${formId})`,
        eventCallback: function () {
          this.processData()
        }.bind(this)
      })
    },
    onKeydownEnter (event) {
      // this method fixes js error when last pass is enabled :/
      // ref. https://www.rockyourcode.com/assertion-failed-input-argument-is-not-an-html-input-element
      event.preventDefault()
      event.stopPropagation()
    },
    onBackClick () {
      this.$router.push({ name: 'result-detail' })
    }
  }
}
</script>
